import { Grid, Typography } from '@mui/material';
import FormModal from 'common/FormModal';
import FormSelectInput from 'common/FormSelectInput';
import FormStyle from 'common/FormStyle';
import FormTextInput from 'common/FormTextInput';
import StickyActionFooter from 'common/StickyActionFooter';
import { toasterNotification } from 'utils/toaster';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState } from 'react';
import NewMember from 'emails/NewMember';
import { sendEmail } from 'utils/methods';
import { businessData as businessDataAtom } from 'atoms';
import { useRecoilState } from 'recoil';
import ReactDOMServer from 'react-dom/server';


const StaffMemberPopup = ({ open, onClose, staffData, getOfficeStaffList, handleDelete, businessId }) => {
    const { token, loading: authLoading } = useAuth();
    const [apiResponse, setApiResponse] = useState();
    const [apiError, setApiError] = useState();
    const [businessData] = useRecoilState(businessDataAtom)
    const [initialFormState, setInitialFormState] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const roleValues = [
        {
            id: 1,
            role: 'Admin',
        },
        {
            id: 2,
            role: 'General',
        },
    ];

    const [formData, setFormData] = useState({
        // first_name: staffData?.row?.first_name || '',
        // last_name: staffData?.row?.last_name || '',
        // email: staffData?.row?.email || '',
        // role_id: staffData?.row?.role || '',

        first_name: '',
        last_name: '',
        email: '',
        role_id: '',
    });

    const [openRole, setOpenRole] = useState(false);
    const handleChange = (e) => {
        const { name, value } = e.target;
        // Trim email value to remove leading/trailing whitespace
        const processedValue = name === 'email' ? value.trim() : value;
        setFormData({ ...formData, [name]: processedValue });
    };

    // Commented out email sending function
    const SendEmailToMember = async(firstName, lastName, email, businessName, businessId) => {
        const emailContent = `
        <!DOCTYPE html>
        ${ReactDOMServer.renderToString(
            <NewMember
                memberName={firstName}
                lastName={lastName}
                businessName={businessName}
                email={email}
                businessId={businessId}
            />,
        )}
    `;
    const { token } = useAuth();
    
    if (!token) {
        console.error('No authentication token found');
        return;
    }

        sendEmail(
            token,
            email,
            'Action Required: Complete Your BeSeen Profile Setup',
            ' ',
            emailContent,
        );
    }

    

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            setIsSubmitting(true);
            
            // Validate access token (only show error if auth is loaded and token is still missing)
            if (authLoading) {
                // Still loading, wait for auth to complete
                setIsSubmitting(false);
                return;
            }
            
            if (!token) {
                console.error('No authentication token found');
                toasterNotification('error', '', 'Authentication error. Please try again.');
                setIsSubmitting(false);
                return;
            }

            // Prepare request body
            const requestBody = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email.toLowerCase(),
                role_id: formData.role_id,
                business_id: staffData ? staffData.row.business_id : businessId
            };

            // Add id if updating existing staff member
            if (staffData && staffData.row?.id) {
                requestBody.id = staffData.row.id;
            }

            // Make API call to backend
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/add-staff-member`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to save staff member';
                toasterNotification('error', '', errorMessage);
                setApiError(result.error || errorMessage);
                setIsSubmitting(false);
                return;
            }

            // If adding new staff member, send email invitation
            if (!staffData && result.success) {
                try {
                    await SendEmailToMember(
                        formData?.first_name,
                        formData?.last_name,
                        formData?.email,
                        businessData?.name,
                        businessData?.id
                    );
                } catch (accountError) {
                    console.error('Error sending email to staff member', accountError);
                }
            }

            setApiResponse(result.data);
            setApiError(null);

            // Reset form and handle success
            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                role_id: '',
            });
            handleSuccess();
        } catch (error) {
            console.error('Error in handleSubmit:', error);
            toasterNotification('error', '', 'An unexpected error occurred. Please try again.');
            setApiError(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSuccess = () => {
        onClose();
        getOfficeStaffList().then();
    };

    const handleDeleteStaff = async () => {
        if (staffData?.row) {
            
            let confirmDelete = window.confirm('Are you sure you want to delete this office staff?');

            if (!confirmDelete) {
                return;
            }

            try {
                if (!token) {
                    console.error('No authentication token found');
                    return;
                }

                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/office-staff`,
                    {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            id: staffData.row.id,
                            business_id: staffData.row.business_id,
                        }),
                    }
                );

                if (response.ok) {
                    handleClose();
                    getOfficeStaffList();
                } else {
                    const result = await response.json().catch(() => ({}));
                    console.error('Error deleting office staff:', result.error || 'Failed to delete office staff');
                }
            } catch (err) {
                console.error('Error deleting staff:', err);
            }
        }
    };

    const handleClose = () => {
        const hasUnsavedChanges = JSON.stringify(formData) !== initialFormState;

        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }
        
        setIsSubmitting(false);
        onClose();
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            role_id: '',
        });
    }

    useEffect(() => {
        if (open) {
            setInitialFormState(JSON.stringify(formData)); // Store initial form data
        }
    }, [open]);

    useEffect(() => {
        const newFormData = {
            first_name: staffData?.row?.first_name || '',
            last_name: staffData?.row?.last_name || '',
            email: staffData?.row?.email || '',
            role_id: staffData?.row?.role_id || '',
            role: staffData?.row?.role_id || ''
        };
        setFormData(newFormData);
        
        // Set initial form state after loading the actual data
        if (staffData?.row) {
            setInitialFormState(JSON.stringify(newFormData));
        }
    }, [staffData?.row]);

    const isFormValid = () => {
        return formData.first_name.trim() !== '' && 
               formData.email.trim() !== '' && 
               formData.role_id !== '';
    };

    return (
        <FormModal 
            open={open} 
            onClose={handleClose} 
            title={staffData ? 'Edit Staff Member' : 'Add a Staff Member'}
            subText={staffData ? '' : 'Add staff members to your business account. They will receive an email invitation to join BeSeen and access your business dashboard.'}
            stickyFooter={
                <StickyActionFooter
                    onCancel={handleClose}
                    onSave={handleSubmit}
                    cancelText="Cancel"
                    saveText={staffData ? "Save" : "Add Staff Member"}
                    isLoading={isSubmitting}
                    disabled={!isFormValid()}
                    showDelete={!!staffData}
                    onDelete={handleDeleteStaff}
                    deleteText="Delete Staff Member"
                />
            }
        >
            <FormStyle onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <FormTextInput
                        formData={formData}
                        label="Name"
                        name="first_name"
                        handleChange={handleChange}
                        placeholder="Enter staff first name"
                        required={true}
                        labelSize={12}
                        textSize={6}
                    />

                    <FormTextInput
                        formData={formData}
                        label="Last Name"
                        name="last_name"
                        handleChange={handleChange}
                        placeholder="Enter staff last name"
                        required={false}
                        noLabel={true}
                        labelSize={12}
                        textSize={6}
                    />

                    <FormTextInput
                        formData={formData}
                        label="Email"
                        name="email"
                        handleChange={handleChange}
                        placeholder="Enter staff email"
                        required={true}
                        labelSize={12}
                        textSize={12}
                    />
                    <FormSelectInput
                        name="role_id"
                        handleChange={handleChange}
                        required={true}
                        inputSize={12}
                        placeholder={'Select a role'}
                        options={roleValues}
                        optionKey="id"
                        optionValue={['role']}
                        value={formData?.role_id}
                        renderValue={(selected) => {
                            if (!selected) {
                                return (
                                    <Typography
                                        color="textSecondary"
                                        style={{
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Select a role
                                    </Typography>
                                );
                            }

                            return roleValues.find((d) => d.id === selected).role;
                        }}
                        noLabel={false}
                        label="Role"
                        helperText={'<strong>Admin:</strong> Full access to business settings and staff management.<br/><strong>General:</strong> Limited access to view and manage appointments.'}
                        openDropdown={openRole}
                        setOpenDropdown={setOpenRole}
                    />
                </Grid>
            </FormStyle>
        </FormModal>
    );
};

export default StaffMemberPopup;
